import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { isAuthorEdit, plainTextToRichHtml } from './squadRichText';

/**
 * Quill 1.x has no line break inside a block: Enter and Shift+Enter both end the
 * paragraph. SQUAD writes the Fondsberater bio as two sentences that belong to one
 * block but want their own line, so both need to be reachable — Enter for a new
 * paragraph (with spacing), Shift+Enter for a plain break (`<br>`, no spacing).
 *
 * The blot below is the established way to give Quill 1.x that break. `value()`
 * returning '\n' is what makes the break survive a delta round trip.
 */
const LINE_BREAK_FORMAT = 'squadBreak';
const QuillEmbed: any = Quill.import('blots/embed');

class SquadLineBreak extends QuillEmbed {
  length() {
    return 1;
  }

  value() {
    return '\n';
  }

  insertInto(parent: any, ref: any) {
    QuillEmbed.prototype.insertInto.call(this, parent, ref);
  }
}

SquadLineBreak.blotName = LINE_BREAK_FORMAT;
SquadLineBreak.tagName = 'BR';
Quill.register(SquadLineBreak);

const BASE_TOOLBAR = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  ['link'],
  ['clean'],
];

const QUILL_FORMATS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'link',
  LINE_BREAK_FORMAT,
];

/**
 * Shift+Enter inserts the break. The second insert covers the end of a block: Quill
 * keeps no trailing text node there, so a single BR would be swallowed. The renderer
 * drops a break that ends a paragraph, so the pair stays invisible in the PDF.
 */
function lineBreakHandler(this: any, range: { index: number }) {
  const quill = this.quill;
  const currentLeaf = quill.getLeaf(range.index)[0];
  const nextLeaf = quill.getLeaf(range.index + 1)[0];
  quill.insertEmbed(range.index, LINE_BREAK_FORMAT, true, 'user');
  if (nextLeaf === null || currentLeaf.parent !== nextLeaf.parent) {
    quill.insertEmbed(range.index, LINE_BREAK_FORMAT, true, 'user');
  }
  quill.setSelection(range.index + 1, 0, 'silent');
}

const ENTER_KEY = 13;

const LINE_BREAK_BINDING = {
  key: ENTER_KEY,
  shiftKey: true,
  handler: lineBreakHandler,
};

/**
 * Registering the binding is not enough. Quill keeps one list per key and walks it
 * until a handler does not return true; its own Enter binding carries `shiftKey: null`,
 * so it matches Shift+Enter as well — and because the defaults are added before the
 * options, it always answered first and ours never ran. Moving ours to the front of
 * the list is the only hook Quill 1.x offers for this.
 */
function preferLineBreakOverParagraph(editor: any): void {
  const bindings = editor?.keyboard?.bindings?.[ENTER_KEY];
  if (!Array.isArray(bindings)) return;
  const index = bindings.findIndex((b: any) => b?.handler === lineBreakHandler);
  if (index > 0) bindings.unshift(bindings.splice(index, 1)[0]);
}

/**
 * The other half. Quill reads HTML through the clipboard matchers, and the built-in
 * one turns every `<br>` into a block change — so a break survived typing but not the
 * next load of the document: reopen it and every break had become a paragraph.
 */
const QuillDelta: any = Quill.import('delta');

const CLIPBOARD_MATCHERS: Array<[string, () => any]> = [
  ['BR', () => new QuillDelta().insert({ [LINE_BREAK_FORMAT]: '' })],
];

/**
 * The form only hears changes with source 'user' (see isAuthorEdit). Snow's link
 * button removes a link with `format('link', false)`, whose source defaults to 'api' —
 * the editor would show the text unlinked while form, preview and PDF kept the link.
 * Same handler, the removal marked as the author's.
 */
const SNOW_LINK_HANDLER: (this: any, value: unknown) => void =
  (Quill.import('themes/snow') as any).DEFAULTS.modules.toolbar.handlers.link;

function linkHandler(this: any, value: unknown) {
  if (value) {
    SNOW_LINK_HANDLER.call(this, value);
    return;
  }
  this.quill.format('link', false, 'user');
}

/**
 * Loading the document is not an edit: without `userOnly` the load sat on the undo
 * stack, and Ctrl+Z right after opening emptied the field — as a 'user' change.
 */
const HISTORY = { userOnly: true };

/**
 * U+00AD marks a place the word may break. It is invisible and cannot be typed on a
 * normal keyboard, so a field that is meant to support hand-placed hyphenation needs
 * a button for it — see germanHyphenation, which treats such a mark as the author's
 * instruction and breaks there rather than where the patterns would.
 */
const SOFT_HYPHEN = '\u00ad';
const SOFT_HYPHEN_BUTTON = 'squadSoftHyphen';

/**
 * U+00A0 holds two words on one line ("31. Dezember", "10 €"). react-pdf breaks on
 * U+0020 only, so the character does the job by itself — but it is invisible and only
 * Mac types it (Option+Space); in a browser there is no Windows shortcut for it. Hence
 * a button, next to the hyphenation one and for the same reason.
 */
const NBSP = '\u00a0';
const NBSP_BUTTON = 'squadNbsp';

type Props = {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
  debounceMs?: number;
  /** Adds the button that inserts a hyphenation point at the cursor. */
  enableSoftHyphen?: boolean;
};

export const SquadRichTextEditor: FC<Props> = ({
  value,
  onChange,
  minHeight = 160,
  debounceMs = 400,
  enableSoftHyphen = false,
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parentRef = useRef(value);
  const quillRef = useRef<ReactQuill | null>(null);
  const [html, setHtml] = useState(() => plainTextToRichHtml(value || ''));

  const insertInvisible = useCallback((char: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const at = editor.getSelection(true)?.index ?? editor.getLength();
    editor.insertText(at, char, 'user');
    editor.setSelection(at + 1, 0, 'user');
  }, []);

  const insertSoftHyphen = useCallback(() => insertInvisible(SOFT_HYPHEN), [insertInvisible]);
  const insertNbsp = useCallback(() => insertInvisible(NBSP), [insertInvisible]);

  const modules = useMemo(
    () => ({
      keyboard: { bindings: { squadLineBreak: LINE_BREAK_BINDING } },
      clipboard: { matchers: CLIPBOARD_MATCHERS },
      history: HISTORY,
      toolbar: enableSoftHyphen
        ? {
            container: [...BASE_TOOLBAR, [SOFT_HYPHEN_BUTTON, NBSP_BUTTON]],
            handlers: {
              link: linkHandler,
              [SOFT_HYPHEN_BUTTON]: insertSoftHyphen,
              [NBSP_BUTTON]: insertNbsp,
            },
          }
        : { container: BASE_TOOLBAR, handlers: { link: linkHandler } },
    }),
    [enableSoftHyphen, insertSoftHyphen, insertNbsp],
  );
  const formats = useMemo(() => QUILL_FORMATS, []);

  useEffect(() => {
    preferLineBreakOverParagraph(quillRef.current?.getEditor());
  }, []);

  // Reset when parent loads a different document value (not our own echo).
  useEffect(() => {
    if (value === parentRef.current) return;
    parentRef.current = value;
    setHtml(plainTextToRichHtml(value || ''));
  }, [value]);

  const handleChange = useCallback(
    (nextHtml: string, _delta: unknown, source: string) => {
      setHtml(nextHtml);
      if (!isAuthorEdit(source)) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        parentRef.current = nextHtml;
        onChange(nextHtml);
      }, debounceMs);
    },
    [onChange, debounceMs],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return (
    <Box
      sx={{
        '& .ql-toolbar.ql-snow': {
          borderColor: 'divider',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        },
        '& .ql-container.ql-snow': {
          borderColor: 'divider',
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          minHeight,
          fontSize: 14,
        },
        [`& .ql-${SOFT_HYPHEN_BUTTON}, & .ql-${NBSP_BUTTON}`]: {
          width: 'auto !important',
          fontSize: 13,
          fontWeight: 700,
        },
        [`& .ql-${SOFT_HYPHEN_BUTTON}`]: { '&::after': { content: '"a-b"' } },
        [`& .ql-${NBSP_BUTTON}`]: { '&::after': { content: '"a\\u2423b"' } },
        '& .ql-editor': {
          minHeight,
          '& a': {
            color: '#0645AD',
            textDecoration: 'underline',
          },
        },
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={html}
        onChange={handleChange}
        modules={modules}
        formats={formats}
      />
    </Box>
  );
};
